import { NavLink, Outlet } from "react-router-dom";

import { cn } from "@/lib/utils";

/**
 * Layout for the `/catalogue` parent route. Renders a header and a
 * sub-nav (Catalogue / Drafts) above the active sub-page's `<Outlet />`.
 *
 * Why a layout component (not a sub-nav embedded in each page): the
 * sub-nav is shared chrome and should stay visually fixed across both
 * sub-pages so a tab switch feels like a tab change, not a full
 * navigation. Mirrors how the top-level nav works in `Layout.tsx` —
 * just one level deeper.
 */
export function CatalogueLayout() {
  return (
    <div className="space-y-6">
      <div>
        <p className="label-mono">Catalogue</p>
        <h1 className="font-display text-4xl text-text">Tools &amp; Services</h1>
      </div>
      <nav className="flex items-center gap-1 border-b border-border">
        <SubNavTab to="" end>
          Catalogue
        </SubNavTab>
        <SubNavTab to="drafts">Drafts</SubNavTab>
      </nav>
      <Outlet />
    </div>
  );
}

function SubNavTab({
  to,
  children,
  end,
}: {
  to: string;
  children: React.ReactNode;
  /** Mark this link as `end` so `/catalogue/drafts` doesn't keep the
   *  Catalogue tab active. Mirrors react-router's nav pattern. */
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "px-4 py-2 -mb-px text-sm font-mono uppercase tracking-label transition-colors",
          isActive
            ? "text-accent border-b-2 border-accent"
            : "text-text-muted hover:text-text border-b-2 border-transparent",
        )
      }
    >
      {children}
    </NavLink>
  );
}
